import React, { FC, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import ContentLoader from 'react-content-loader';
import theme from 'src/shared/theme';
import { Checkbox } from 'src/components/widgets/checkbox';
import { T } from 'src/shared/helpers';
import { TOOL_DEFINITIONS } from '../common/constants';
import type { areaBackgroundRuleEditor_AreaBackgroundRuleDetailQuery } from './__generated__/areaBackgroundRuleEditor_AreaBackgroundRuleDetailQuery.graphql';
import type { areaBackgroundRuleEditor_UpdateAreaBackgroundRuleMutation } from './__generated__/areaBackgroundRuleEditor_UpdateAreaBackgroundRuleMutation.graphql';
import { StackedBreadcrumb, StackedBody, SectionCard, SectionCardTitle, SharedWarningCallout, SettingHint, EmptyRulesCenter } from '../common/styled';

export interface AreaBackgroundRuleEditorProps {
    itemId: string;
    productId: string;
    toolKey: string;
    onBack: () => void;
    onSave: () => void;
    saveRef: React.MutableRefObject<(() => Promise<void>) | null>;
    onCanSaveChange?: (canSave: boolean) => void;
    onSwitchToFullMode?: () => void;
}

const areaBackgroundRuleDetailQuery = graphql`
    query areaBackgroundRuleEditor_AreaBackgroundRuleDetailQuery($id: ID!) {
        areaBackgroundRule(id: $id) {
            id
            areaBackgroundRuleID
            name
            colorList
            enableNoColor
            enableImageCustomerGallery
            enableImageSellerGallery
            imageGalleryCustomerFormat
            productsSides {
                id
                productID
                sideID
            }
            printTypeIDs
        }
    }
`;

const AreaBackgroundRuleEditorDetail: FC<AreaBackgroundRuleEditorProps & { ruleId: string }> = ({
    ruleId,
    productId,
    toolKey,
    onBack,
    onSave,
    saveRef,
    onCanSaveChange,
    onSwitchToFullMode
}) => {
    const toolName = TOOL_DEFINITIONS.find((tool) => tool.key === toolKey)?.name || '';
    const productNameData = useLazyLoadQuery<any>(
        graphql`
            query areaBackgroundRuleEditor_ProductNameQuery($id: ID!) {
                node(id: $id) {
                    ... on Model {
                        name
                    }
                }
            }
        `,
        { id: productId }
    );
    const productName = (productNameData?.node as any)?.name || '';

    const json = useLazyLoadQuery<areaBackgroundRuleEditor_AreaBackgroundRuleDetailQuery>(areaBackgroundRuleDetailQuery, { id: ruleId });
    const rule = json?.areaBackgroundRule;
    const ruleName = rule?.name || '';
    const assignedProductCount = rule?.productsSides ? new Set(rule.productsSides.map((productSide) => productSide.productID)).size : undefined;

    const ruleRef = useRef<any>(rule);
    const [enableColor, setEnableColor] = useState(() => rule?.colorList != null);
    const [enableUploadImage, setEnableUploadImage] = useState(() => rule?.enableImageCustomerGallery || false);
    const [enableImagesGallery, setEnableImagesGallery] = useState(() => rule?.enableImageSellerGallery || false);

    const [commitUpdateAreaBg] = useMutation<areaBackgroundRuleEditor_UpdateAreaBackgroundRuleMutation>(graphql`
        mutation areaBackgroundRuleEditor_UpdateAreaBackgroundRuleMutation($input: AreaBackgroundRuleToolInput!) {
            areaBackgroundRuleSave(ruleInput: $input) {
                id
            }
        }
    `);

    const handleSave = useCallback(async () => {
        if (!ruleRef.current) return;
        try {
            const input: any = {
                id: ruleRef.current.id,
                name: ruleRef.current.name,
                colorList: enableColor ? (ruleRef.current.colorList ?? []) : null,
                enableNoColor: ruleRef.current.enableNoColor || false,
                enableImageCustomerGallery: enableUploadImage,
                enableImageSellerGallery: enableImagesGallery,
                imageGalleryCustomerFormat: enableUploadImage ? 2 : ruleRef.current.imageGalleryCustomerFormat || 0,
                productsSides: (ruleRef.current.productsSides || []).map((productSide: any) => ({
                    id: productSide.id,
                    sideGlobalID: productSide.sideID ? btoa(`Side:${productSide.sideID}`) : null
                })),
                printTypeIDs: ruleRef.current.printTypeIDs || []
            };
            await new Promise<void>((resolve, reject) => {
                commitUpdateAreaBg({
                    variables: { input },
                    onCompleted: () => {
                        onSave();
                        resolve();
                    },
                    onError: (error) => reject(error)
                });
            });
        } catch {
            /* ignore */
        }
    }, [enableColor, enableUploadImage, enableImagesGallery, onSave, commitUpdateAreaBg]);

    saveRef.current = handleSave;

    useEffect(() => {
        onCanSaveChange?.(enableColor || enableUploadImage || enableImagesGallery);
    }, [enableColor, enableUploadImage, enableImagesGallery, onCanSaveChange]);

    return (
        <>
            <StackedBreadcrumb>
                <span onClick={onBack}>{productName}</span> / {toolName} / {ruleName}
            </StackedBreadcrumb>
            <StackedBody>
                {assignedProductCount != null && assignedProductCount > 1 && (
                    <SharedWarningCallout>
                        {T._('This rule is assigned to', 'Admin')} <strong>{assignedProductCount}</strong>{' '}
                        {T._('products. Any changes will apply to all of them.', 'Admin')}
                    </SharedWarningCallout>
                )}
                <SectionCard>
                    <SectionCardTitle>{T._('Fill options', 'Admin')}</SectionCardTitle>

                    <div style={{ marginBottom: 16 }}>
                        <Checkbox label={T._('Color', 'Admin')} checked={enableColor} onChange={(checked) => setEnableColor(checked)} />
                        {enableColor && <SettingHint>{T._('Color picker and palette settings are available in full mode.', 'Admin')}</SettingHint>}
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <Checkbox label={T._('Upload image', 'Admin')} checked={enableUploadImage} onChange={(checked) => setEnableUploadImage(checked)} />
                    </div>

                    <div style={{ marginBottom: 0 }}>
                        <Checkbox
                            label={T._('Background gallery', 'Admin')}
                            checked={enableImagesGallery}
                            onChange={(checked) => setEnableImagesGallery(checked)}
                        />
                    </div>
                    {enableImagesGallery && onSwitchToFullMode && (
                        <SettingHint>
                            <a style={{ cursor: 'pointer' }} onClick={onSwitchToFullMode}>
                                {T._('Manage gallery', 'Admin')} &#8250;
                            </a>
                        </SettingHint>
                    )}
                </SectionCard>
            </StackedBody>
        </>
    );
};

export const AreaBackgroundRuleEditor: FC<AreaBackgroundRuleEditorProps> = (props) => {
    if (!props.itemId) {
        return (
            <>
                <StackedBreadcrumb>
                    <span onClick={props.onBack}>{T._('Product', 'Admin')}</span> / {T._('Rule', 'Admin')}
                </StackedBreadcrumb>
                <StackedBody>
                    <EmptyRulesCenter>{T._('Rule not found', 'Admin')}</EmptyRulesCenter>
                </StackedBody>
            </>
        );
    }

    return (
        <Suspense
            fallback={
                <>
                    <StackedBreadcrumb>
                        <span onClick={props.onBack}>{T._('Loading...', 'Admin')}</span>
                    </StackedBreadcrumb>
                    <StackedBody>
                        <ContentLoader
                            speed={2}
                            width='100%'
                            height='60'
                            backgroundColor={theme.skeleton.backgroundColor}
                            foregroundColor={theme.skeleton.foregroundColor}
                        >
                            <rect x='0' y='0' rx='8' ry='8' width='100%' height='16' />
                            <rect x='0' y='24' rx='8' ry='8' width='80%' height='16' />
                            <rect x='0' y='48' rx='8' ry='8' width='60%' height='16' />
                        </ContentLoader>
                    </StackedBody>
                </>
            }
        >
            <AreaBackgroundRuleEditorDetail {...props} ruleId={props.itemId} />
        </Suspense>
    );
};
